import React, { useState, useEffect } from 'react';
import { AgentCreateRequest, AgentUpdateRequest, AgentConfig, AgentType } from '../../../shared/types/agent';
import { agentApi } from '../../services/agentApi';

interface AgentFormProps {
  onSubmit: (data: AgentCreateRequest | AgentUpdateRequest) => void;
  onCancel: () => void;
  loading: boolean;
  initialData?: AgentConfig;
}

const AGENT_TYPES: { value: AgentType; label: string; description: string }[] = [
  { value: 'orchestrator', label: 'Orchestrator', description: 'Coordinates and manages other agents' },
  { value: 'researcher', label: 'Researcher', description: 'Gathers and validates information' },
  { value: 'analyzer', label: 'Analyzer', description: 'Analyzes data and identifies patterns' },
  { value: 'writer', label: 'Writer', description: 'Creates content and documentation' },
  { value: 'executor', label: 'Executor', description: 'Executes tasks and builds artifacts' },
];

export const AgentForm: React.FC<AgentFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  initialData,
}) => {
  const [formData, setFormData] = useState<AgentCreateRequest>({
    name: initialData?.name || '',
    type: initialData?.type || 'orchestrator',
    description: initialData?.description || '',
    objective: initialData?.objective || '',
    prompt: initialData?.prompt || '',
    systemPrompt: initialData?.systemPrompt || '',
    tools: initialData?.tools?.map(t => t.id) || [],
    chatSessions: initialData?.chatSessions || [],
  });

  const [availableTools, setAvailableTools] = useState<{ internal: any[], mcp: any[] }>({ internal: [], mcp: [] });
  const [availableSessions, setAvailableSessions] = useState<any[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  useEffect(() => {
    loadAvailableTools();
    loadAvailableSessions();
  }, []);

  const loadAvailableTools = async () => {
    try {
      setToolsLoading(true);
      const response = await agentApi.getAvailableTools();
      if (response.success && response.data) {
        setAvailableTools(response.data);
      }
    } catch (err) {
      console.error('Failed to load tools:', err);
    } finally {
      setToolsLoading(false);
    }
  };

  const loadAvailableSessions = async () => {
    try {
      const response = await agentApi.getChatSessions();
      if (response.success && response.data) {
        setAvailableSessions(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setAvailableSessions([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleToolToggle = (toolId: string) => {
    setFormData(prev => ({
      ...prev,
      tools: (prev.tools || []).includes(toolId)
        ? (prev.tools || []).filter(id => id !== toolId)
        : [...(prev.tools || []), toolId]
    }));
  };

  const handleSessionToggle = (sessionId: string) => {
    setFormData(prev => ({
      ...prev,
      chatSessions: (prev.chatSessions || []).includes(sessionId)
        ? (prev.chatSessions || []).filter(id => id !== sessionId)
        : [...(prev.chatSessions || []), sessionId]
    }));
  };

  const selectedAgentType = AGENT_TYPES.find(t => t.value === formData.type);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white mb-2">Create New Agent</h2>
          <p className="text-gray-400">Configure your AI agent with tools and chat session attachments</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Agent Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter agent name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Agent Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as AgentType }))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                >
                  {AGENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {selectedAgentType && (
                  <p className="text-xs text-gray-400 mt-1">{selectedAgentType.description}</p>
                )}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                rows={3}
                placeholder="Brief description of the agent's purpose"
              />
            </div>
          </div>

          {/* Agent Configuration */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Agent Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Objective *
                </label>
                <textarea
                  value={formData.objective}
                  onChange={(e) => setFormData(prev => ({ ...prev, objective: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  rows={3}
                  placeholder="What should this agent accomplish?"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Prompt
                </label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  rows={4}
                  placeholder="Custom prompt for the agent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  System Prompt
                </label>
                <textarea
                  value={formData.systemPrompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, systemPrompt: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  rows={4}
                  placeholder="System-level instructions for the agent"
                />
              </div>
            </div>
          </div>

          {/* Tool Selection */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Available Tools</h3>
            {toolsLoading ? (
              <div className="text-center text-gray-400 py-4">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading tools...
              </div>
            ) : (
              <div className="space-y-4">
                {/* Internal Tools */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Internal Tools</h4>
                  <div className="space-y-2">
                    {availableTools.internal.map((tool) => (
                      <label key={tool.id} className="flex items-center space-x-3 p-2 bg-gray-700 rounded hover:bg-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.tools || []).includes(tool.id)}
                          onChange={() => handleToolToggle(tool.id)}
                          className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm text-white">{tool.name}</div>
                          <div className="text-xs text-gray-400">{tool.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* MCP Tools */}
                {availableTools.mcp.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">MCP Tools</h4>
                    <div className="space-y-2">
                      {availableTools.mcp.map((tool) => (
                        <label key={tool.id} className="flex items-center space-x-3 p-2 bg-gray-700 rounded hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(formData.tools || []).includes(tool.id)}
                            onChange={() => handleToolToggle(tool.id)}
                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm text-white">{tool.name}</div>
                            <div className="text-xs text-gray-400">{tool.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chat Session Attachment */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Chat Sessions</h3>
            <p className="text-sm text-gray-400 mb-4">Attach this agent to specific chat sessions</p>
            <div className="space-y-2">
              {Array.isArray(availableSessions) && availableSessions.map((session) => (
                <label key={session.id} className="flex items-center space-x-3 p-2 bg-gray-700 rounded hover:bg-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(formData.chatSessions || []).includes(session.id)}
                    onChange={() => handleSessionToggle(session.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white">{session.name || session.id}</div>
                    <div className="text-xs text-gray-400">{session.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name || !formData.objective}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded"
            >
              {loading ? 'Creating...' : 'Create Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
